import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { useNuxtApp } from 'nuxt/app';
import { createThrottledSendBannerViewInteraction } from '~~/common/utils/bannerInteractionAnalytics';
import { AnalyticsBannerInteraction, EMPTY_ANALYTICS_BANNER_INTERACTION } from '~~/common/utils/AnalyticsBannerInteraction';
import type { BannerInteractionInput } from '~~/common/utils/AnalyticsBannerInteraction';

interface CarouselContentImage {
  url?: string | null;
  alt?: string | null;
}

export interface CarouselComponent {
  campaignName?: string | null;
  link?: string | null;
  media?: {
    contentImage?: CarouselContentImage | null;
  } | null;
  isWebpMissing?: boolean;
  isImageMissing?: boolean;
}

export interface CarouselContent {
  id?: string;
  components: CarouselComponent[];
}

export function useAwCarouselV3 ({ content }: { content: Ref<CarouselContent> }) {
  const duration = 3500;
  let time = duration;
  let interval: ReturnType<typeof setInterval> | undefined = undefined;
  let hoverCarouselElement = false;
  const paused = ref(false);
  const activeIndex = ref(0);
  const contents = computed(() => {
    return content.value.components;
  });
  const previousIndex = computed(() => {
    return (activeIndex.value === 0
      ? contents.value.length - 1
      : activeIndex.value - 1
    );
  });
  onMounted(() => {
    time = duration;
    startInterval();
  });
  onBeforeUnmount(() => {
    clearInterval(interval);
  });

  return {
    paused,
    activeIndex,
    previousIndex,
    contents,
    next,
    prev,
    togglePause,
    handleMouseEnter,
    handleMouseLeave,
    onImageError,
    getContentImage,
    calculateBackgroundImage,
  };
  function startInterval () {
    const precision = 100;
    interval = setInterval(() => {
      if (!hoverCarouselElement && !paused.value) {
        time -= precision;
      }
      if (time <= 0) {
        next();
      }
    }, precision);
  }
  function goToIndex (index: number) {
    activeIndex.value = index;
    time = duration;
  }
  function next () {
    let nextIndex = activeIndex.value + 1;

    // Go to the first image if the active
    // image ist the last one.
    if (!contents.value[nextIndex]) {
      nextIndex = 0;
    }

    goToIndex(nextIndex);
  }
  function prev () {
    let nextIndex = activeIndex.value - 1;

    // Go to the last image if the active
    // image is the first one.
    if (!contents.value[nextIndex]) {
      nextIndex = contents.value.length - 1;
    }

    goToIndex(nextIndex);
  }
  function togglePause () {
    paused.value = !paused.value;
  }
  function handleMouseEnter () {
    hoverCarouselElement = true;
  }
  function handleMouseLeave () {
    hoverCarouselElement = false;
  }
  function onImageError (event: Event, cmsComponent: CarouselComponent) {
    const target = event.target;
    if (target instanceof HTMLImageElement && target.src.endsWith('.webp')) {
      cmsComponent.isWebpMissing = true;
    } else {
      cmsComponent.isImageMissing = true;
    }
  }
  function getContentImage (cmsComponent: CarouselComponent) {
    const { $addWebpPostfixIfSupported } = useNuxtApp();
    const url = cmsComponent?.media?.contentImage?.url;
    return {
      url: (cmsComponent.isWebpMissing || typeof url !== 'string'
        ? url
        : $addWebpPostfixIfSupported({
          url,
          webpUrl: `${url}.webp`,
        })
      ),
      alt: cmsComponent?.media?.contentImage?.alt,
    };
  }
  function calculateBackgroundImage (cmsComponent: CarouselComponent) {
    return (
      'linear-gradient(to bottom, rgba(0,0,0,.32), rgba(0,0,0,.32)), ' +
      `url('${getContentImage(cmsComponent).url}')`
    );
  }
}

export function useAwCarouselV3BannerAnalytics ({
  contents,
  activeIndex,
  analyticsMeasurable,
  analyticsBannerInteraction,
}: {
  contents: ComputedRef<CarouselComponent[]>;
  activeIndex: Ref<number>;
  analyticsMeasurable: ComputedRef<boolean | undefined>;
  analyticsBannerInteraction: ComputedRef<BannerInteractionInput>;
}) {
  const campaignName = computed(() => {
    return contents.value?.[activeIndex.value]?.campaignName || null;
  });
  const extendedInteraction = computed(() => {
    const interaction = analyticsBannerInteraction.value;
    if (interaction === EMPTY_ANALYTICS_BANNER_INTERACTION) {
      return EMPTY_ANALYTICS_BANNER_INTERACTION;
    } else if (!AnalyticsBannerInteraction.isValid(interaction)) {
      return interaction;
    } else {
      return AnalyticsBannerInteraction.create({
        ...interaction,
        promotionName: campaignName.value || null,
        promotionBannerIndex: activeIndex.value + 1,
      });
    }
  });
  onMounted(() => {
    const throttledSendBannerViewInteraction = createThrottledSendBannerViewInteraction();
    watch(
      activeIndex,
      () => {
        throttledSendBannerViewInteraction({
          analyticsMeasurable: analyticsMeasurable.value,
          analyticsBannerInteraction: extendedInteraction.value,
        });
      },
      {
        immediate: true,
      },
    );
  });
  return {
    analyticsBannerInteraction: extendedInteraction,
  };
}
